// Top info panel for portrait layouts. The playfield is pinned to the bottom of the screen
// in portrait, leaving an empty band above; this controller fills that band with a DOM overlay
// (song info on song select, score/progress in-game). Landscape hides it entirely.

import type { Difficulty, SongEntry } from '../data/types';
import type { SongRecord } from '../data/records';
import { withBase } from '../core/paths';

const MIF = withBase('/dataset/tex_l44av_music_information');

const DIFF_LABEL_URL: Record<Difficulty, string> = {
  BSC: `${MIF}/MIF_DIF_00.webp`,
  ADV: `${MIF}/MIF_DIF_10.webp`,
  EXT: `${MIF}/MIF_DIF_20.webp`,
};

function mifLevelUrl(level: number): string {
  if (!Number.isFinite(level) || level <= 0) return '';
  const intPart = Math.floor(level);
  const decPart = Math.round((level - intPart) * 10);
  if (intPart >= 11) return `${MIF}/MIF_LV_10_9.webp`;
  if (intPart === 10 && decPart === 0) return `${MIF}/MIF_LV_10.webp`;
  if (intPart === 10) return `${MIF}/MIF_LV_10_${decPart}.webp`;
  if (intPart === 9 && decPart === 0) return `${MIF}/MIF_LV_9.webp`;
  if (intPart === 9) return `${MIF}/MIF_LV_9_${decPart}.webp`;
  return `${MIF}/MIF_LV_${intPart}.webp`;
}

interface SongSelectInfo {
  song: SongEntry | null;
  diff: Difficulty;
  record: SongRecord | null;
}

interface GameplayInfo {
  score: number;
  rank: string;
  progress: number; // 0..1
  combo: number;
  judgement: Record<string, number>;
  song?: SongEntry | null;
  diff?: Difficulty | null;
}

class InfoPanelImpl {
  private root: HTMLElement;
  private visible = false;
  // Cached gameplay DOM refs so per-frame updates only touch the changed
  // fields instead of regenerating innerHTML at 30Hz.
  private gp: {
    scoreEl: HTMLElement;
    rankEl: HTMLElement;
    progressFill: HTMLElement;
    comboEl: HTMLElement;
    countP: HTMLElement;
    countG: HTMLElement;
    countGd: HTMLElement;
    countM: HTMLElement;
  } | null = null;
  // jubeat-style rolling score counter — displayed value chases target.
  private scoreDisplayed = 0;
  private scoreTarget = 0;
  private scoreRaf: number | null = null;

  constructor() {
    let el = document.getElementById('info-panel');
    if (!el) {
      el = document.createElement('div');
      el.id = 'info-panel';
      const app = document.getElementById('app');
      app?.parentElement?.insertBefore(el, app);
    }
    this.root = el;
    this.root.style.display = 'none';
  }

  /** Reposition the panel to fill the space ABOVE the playfield. Called from Game.applyLayout. */
  setLayout(opts: { playfieldHeightPx: number; portrait: boolean }) {
    if (!opts.portrait || opts.playfieldHeightPx >= window.innerHeight - 8) {
      this.visible = false;
      this.root.style.display = 'none';
      return;
    }
    this.visible = true;
    this.root.style.display = 'flex';
    const panelH = Math.max(0, window.innerHeight - opts.playfieldHeightPx);
    const panelW = window.innerWidth;
    this.root.style.height = `${panelH}px`;
    // Pick a layout variant based on the panel's aspect ratio:
    //   - ultra wide (ratio > 6)      → is-row: everything on one row
    //   - wide (3 < ratio ≤ 6)        → is-wide: diff+title column, BPM/record on the right
    //   - near-square (ratio < 1.3)   → is-stacked: cover on top, text below
    //   - otherwise                   → default split (cover left, info column right)
    const ratio = panelH > 0 ? panelW / panelH : 1;
    const row = ratio > 6;
    const wide = !row && ratio > 3;
    const stacked = !wide && !row && ratio < 1.3;
    this.root.classList.toggle('is-row', row);
    this.root.classList.toggle('is-wide', wide);
    this.root.classList.toggle('is-stacked', stacked);
  }

  clear() {
    this.root.innerHTML = '';
    this.resetGameplayState();
  }

  /** Forcibly hide the panel (display:none) until Game.applyLayout re-evaluates.
   *  Use when a screen wants the full viewport in portrait. */
  hide() {
    this.root.innerHTML = '';
    this.visible = false;
    this.root.style.display = 'none';
    this.resetGameplayState();
  }

  private resetGameplayState() {
    this.gp = null;
    this.scoreDisplayed = 0;
    this.scoreTarget = 0;
    if (this.scoreRaf != null) {
      cancelAnimationFrame(this.scoreRaf);
      this.scoreRaf = null;
    }
  }

  setSongSelect(info: SongSelectInfo) {
    // Leaving any in-progress gameplay session — drop the cached refs and ticker.
    if (this.gp) this.resetGameplayState();
    const { song, diff, record } = info;
    if (!song) {
      this.root.innerHTML = `<div class="info-empty">곡을 선택해주세요</div>`;
      return;
    }
    const bpm = song.bpm
      ? (song.bpm.min && song.bpm.max && song.bpm.min !== song.bpm.max
        ? `${song.bpm.min} – ${song.bpm.max}`
        : String(song.bpm.max ?? song.bpm.min ?? '?'))
      : '?';

    const diffData = song.difficulties[diff];
    const level = diffData?.detail ?? diffData?.level ?? 0;
    const diffLabelUrl = DIFF_LABEL_URL[diff];
    const lvLabelUrl = `${MIF}/MIF_LV_0.webp`;
    const lvNumUrl = mifLevelUrl(level);
    const coverUrl = song.paths?.cover ? withBase(song.paths.cover) : '';

    const recHtml = record
      ? `<div class="ip-record">
           <div class="ip-record__rank">${escapeHtml(record.rank)}</div>
           <div class="ip-record__col">
             <div class="ip-record__score">${record.score.toString().padStart(7, '0')}</div>
             <div class="ip-record__tag">최고 기록 · ${diff}${record.fullCombo ? ' · FULL COMBO' : ''}</div>
           </div>
         </div>`
      : `<div class="ip-record ip-record--empty">아직 기록 없음 · ${diff}</div>`;

    this.root.innerHTML = `
      <div class="ip-cover">
        ${coverUrl ? `<img src="${escapeAttr(coverUrl)}" alt="" />` : ''}
      </div>
      <div class="ip-right">
        <div class="ip-diff">
          <img class="ip-diff__label" src="${escapeAttr(diffLabelUrl)}" alt="${diff}" />
          <img class="ip-diff__lv" src="${escapeAttr(lvLabelUrl)}" alt="LEVEL" />
          ${lvNumUrl ? `<img class="ip-diff__num" src="${escapeAttr(lvNumUrl)}" alt="${level}" />` : ''}
        </div>
        <div class="ip-title" title="${escapeAttr(song.title ?? '')}">${escapeHtml(song.title ?? 'Untitled')}</div>
        <div class="ip-artist">${escapeHtml(song.artist ?? '')}</div>
        <div class="ip-bpm">BPM ${escapeHtml(bpm)}</div>
        ${recHtml}
      </div>
    `;
  }

  setGameplay(info: GameplayInfo) {
    const pct = Math.max(0, Math.min(1, info.progress));
    const j = info.judgement;
    if (!this.gp) this.buildGameplayDom(info.song ?? null, info.diff ?? null);
    const gp = this.gp!;
    this.scoreTarget = info.score;
    this.ensureScoreTicker();
    gp.rankEl.textContent = info.rank;
    gp.progressFill.style.width = `${(pct * 100).toFixed(1)}%`;
    gp.comboEl.textContent = `${info.combo} combo`;
    gp.countP.textContent  = `P ${j.perfect ?? 0}`;
    gp.countG.textContent  = `G ${j.great ?? 0}`;
    gp.countGd.textContent = `Gd ${j.good ?? 0}`;
    gp.countM.textContent  = `M ${j.miss ?? 0}`;
  }

  private buildGameplayDom(song: SongEntry | null, diff: Difficulty | null) {
    const cover = song?.paths?.cover ? withBase(song.paths.cover) : '';
    const songMeta = song
      ? `<div class="play-song">
           <div class="play-song__cover">${cover ? `<img src="${escapeAttr(cover)}" alt="" />` : ''}</div>
           <div class="play-song__text">
             <div class="play-song__title" title="${escapeAttr(song.title ?? '')}">${escapeHtml(song.title ?? 'Untitled')}</div>
             <div class="play-song__artist">${escapeHtml(song.artist ?? '')}</div>
             ${diff ? `<div class="play-song__diff">${escapeHtml(diff)}</div>` : ''}
           </div>
         </div>`
      : '';
    this.root.innerHTML = `
     <div class="play-wrap">
      ${songMeta}
      <div class="play-score">0000000</div>
      <div class="play-rank"></div>
      <div class="play-progress"><div class="play-progress__fill" style="width:0%"></div></div>
      <div class="play-combo">0 combo</div>
      <div class="play-counts">
        <span class="jc jc--p">P 0</span>
        <span class="jc jc--g">G 0</span>
        <span class="jc jc--gd">Gd 0</span>
        <span class="jc jc--m">M 0</span>
      </div>
     </div>
    `;
    this.gp = {
      scoreEl:      this.root.querySelector('.play-score') as HTMLElement,
      rankEl:       this.root.querySelector('.play-rank') as HTMLElement,
      progressFill: this.root.querySelector('.play-progress__fill') as HTMLElement,
      comboEl:      this.root.querySelector('.play-combo') as HTMLElement,
      countP:       this.root.querySelector('.jc--p') as HTMLElement,
      countG:       this.root.querySelector('.jc--g') as HTMLElement,
      countGd:      this.root.querySelector('.jc--gd') as HTMLElement,
      countM:       this.root.querySelector('.jc--m') as HTMLElement,
    };
    this.scoreDisplayed = 0;
    this.scoreTarget = 0;
  }

  /** Animate the on-screen score toward `scoreTarget`. Each rAF step closes
   *  ~18% of the remaining gap (≈12 frames / 200ms to converge), matching the
   *  jubeat-style "ticking up" feel. */
  private ensureScoreTicker() {
    if (this.scoreRaf != null) return;
    const tick = () => {
      this.scoreRaf = null;
      if (!this.gp) return;
      const diff = this.scoreTarget - this.scoreDisplayed;
      if (Math.abs(diff) < 1) {
        this.scoreDisplayed = this.scoreTarget;
      } else {
        const step = Math.max(1, Math.ceil(Math.abs(diff) * 0.18));
        this.scoreDisplayed += diff > 0 ? step : -step;
      }
      this.gp.scoreEl.textContent = Math.round(this.scoreDisplayed).toString().padStart(7, '0');
      if (this.scoreDisplayed !== this.scoreTarget) {
        this.scoreRaf = requestAnimationFrame(tick);
      }
    };
    this.scoreRaf = requestAnimationFrame(tick);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function escapeAttr(s: string): string { return escapeHtml(s); }

let instance: InfoPanelImpl | null = null;
export function infoPanel(): InfoPanelImpl {
  if (!instance) instance = new InfoPanelImpl();
  return instance;
}
